/* =========================================================
   Alta de cuentas en lote, desde una plantilla.

   Hasta ahora esto solo se podía hacer por SSH, con el script
   `provision-users`. Sirve para el lanzamiento —lo hace un técnico, una
   vez— pero no para el día a día de ETC: dar de alta a los cinco
   instructores de un socio nuevo no debería exigir una terminal.

   La plantilla lleva DOS hojas. La primera son los datos. La segunda es
   el catálogo de organizaciones y sites con sus identificadores, para
   que quien la rellene copie y pegue en vez de teclear de memoria: el
   identificador equivocado es, con diferencia, el error más común, y el
   que peor se explica desde el otro lado del teléfono.

   Dos pasos siempre: comprobar y aplicar. Nadie debería crear
   trescientas cuentas sin ver antes cuántas son nuevas, cuántas ya
   existían y qué filas están mal escritas.
   ========================================================= */
import React, { useState } from 'react';
import { ic, sized } from '@/components/Icons';
import { Section } from '@/components/ui';
import { useI18n } from '@/i18n';
import { ds } from '@/data';
import { api } from '@/lib/api';
import { downloadSheets, parseSpreadsheet } from '@/lib/xlsx';
import { useAppStore } from '@/store/useAppStore';

/* ---------------- la plantilla ---------------- */

const COLUMNAS = ['Correo', 'Nombre', 'Apellido', 'Rol', 'Organización (ATC)', 'Sites'] as const;

/** Sinónimos de cada columna, para que una plantilla retocada a mano
 *  —o traducida— siga entrando. Todo en minúsculas y sin acentos. */
const ALIAS: Record<(typeof COLUMNAS)[number], string[]> = {
  'Correo': ['correo', 'email', 'e-mail', 'mail'],
  'Nombre': ['nombre', 'first name', 'first', 'nombres'],
  'Apellido': ['apellido', 'last name', 'last', 'apellidos'],
  'Rol': ['rol', 'role', 'perfil'],
  'Organización (ATC)': ['organizacion (atc)', 'organizacion', 'organization', 'atc', 'org', 'socio'],
  'Sites': ['sites', 'site', 'centro', 'centros'],
};

const sinAcentos = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

/** Empareja las cabeceras del archivo con nuestras columnas. */
function mapaCabeceras(headers: string[]): Partial<Record<(typeof COLUMNAS)[number], string>> {
  const out: Partial<Record<(typeof COLUMNAS)[number], string>> = {};
  for (const col of COLUMNAS) {
    const nombres = ALIAS[col];
    const h = headers.find((x) => nombres.includes(sinAcentos(x)));
    if (h) out[col] = h;
  }
  return out;
}

/** Los identificadores se escriben separados por coma, punto y coma o
 *  espacios: quien rellena una hoja no tiene por qué acertar el
 *  separador que esperábamos. */
const partirIds = (v: string | undefined) =>
  String(v ?? '').split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);

/* ---------------- lo que contesta el servidor ---------------- */

interface Problema { fila: number; email: string; motivo: string }

interface CandidatoVista {
  filas: number[];
  email: string; nombre: string; role: string; ambito: string[];
  /** Ya hay una cuenta con ese correo. */
  existe: boolean;
  /** Lo que esa cuenta tiene HOY. Solo viene cuando ya existe.
   *
   *  Es lo que permite avisar de lo que se pierde: el ámbito se reemplaza
   *  entero, así que restablecer a alguien que hoy cubre tres
   *  organizaciones y aparece en la plantilla con una lo deja con una. */
  actual?: { role: string; ambito: string[] };
}

interface Previo {
  problemas: Problema[];
  nuevas: number;
  existentes: number;
  candidatos: CandidatoVista[];
  puedeInvitar: boolean;
}

interface FilaResultado {
  email: string; nombre: string; role: string; ambito: string;
  entrega: 'invitacion' | 'temporal';
  filas?: number[];
  password?: string;
  estado: 'creada' | 'restablecida' | 'omitida';
  /** Solo por invitación: ¿salió el correo? Si no, esa cuenta existe pero
   *  no tiene contraseña ni enlace, y nadie puede entrar en ella. */
  invitada?: boolean;
}

interface Resumen {
  filas: FilaResultado[];
  creadas: number; restablecidas: number; omitidas: number; lote: string;
  /** Cuentas creadas cuya invitación no se pudo mandar. */
  sinInvitar?: number;
}

interface FilaEnviada {
  fila: number; email: string; first: string; last: string; rol: string; ambito: string[];
}

/* ---------------- la tabla de resultados ---------------- */

type Tono = 'ok' | 'warn' | 'err' | 'neutro';

const COLOR: Record<Tono, string> = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  err: 'var(--err)',
  neutro: 'var(--text-2)',
};

/** El rol con las palabras del portal. En la tabla se lee una fila
 *  detrás de otra: «PARTNER_MGR» obliga a traducir mentalmente. */
const ROL_TEXTO = (role: string, es: boolean): string =>
  role === 'PARTNER_MGR' ? (es ? 'Gerente del socio' : 'Partner manager')
  : role === 'SITE_ADMIN' ? 'Site Admin'
  : role === 'SITE_MGR' ? 'Site Manager'
  : role;

interface FilaVista {
  clave: string;
  /** Número de fila en la hoja. Texto, porque una cuenta puede venir de
   *  varias filas cuando el correo se repite. */
  fila: string;
  email: string;
  nombre: string;
  rol: string;
  ambito: string;
  estado: string;
  motivo?: string;
  tono: Tono;
}

interface Vista {
  resumen: { clave: string; n: number; texto: string; tono: Tono }[];
  filas: FilaVista[];
  aviso?: string;
  avisoGrave?: boolean;
}

/* ---------------- pantalla ---------------- */

export function AltasMasivasCard() {
  const { lang } = useI18n();
  const es = lang === 'es';
  const addToast = useAppStore((s) => s.addToast);

  const [file, setFile] = useState<File | null>(null);
  const [filas, setFilas] = useState<FilaEnviada[] | null>(null);
  const [previo, setPrevio] = useState<Previo | null>(null);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [lote, setLote] = useState('');
  const [restablecer, setRestablecer] = useState(false);
  /** Cómo llega cada persona a su cuenta.
   *
   *  Por defecto la invitación, cuando el portal puede mandarla: así ETC
   *  no llega a conocer la contraseña de nadie. Con contraseña temporal es
   *  inevitable —hay que dictársela— y además hay que entregarla a mano,
   *  una por una. Si no hay correo, esta elección no se ofrece. */
  const [entrega, setEntrega] = useState<'invitacion' | 'temporal'>('invitacion');
  const [busy, setBusy] = useState(false);
  /** El panel de resultados queda por debajo del pliegue después de
   *  aplicar, y el botón de descargar las contraseñas con él. Hay que
   *  llevar la vista hasta ahí: esa descarga es la única copia. */
  const panel = React.useRef<HTMLDivElement>(null);

  /** La plantilla: una hoja para rellenar y otra con los identificadores
   *  que se pueden usar, sacados de lo que hay hoy en el padrón. */
  function descargarPlantilla() {
    const ejemplo = [
      ['ana.delgado@ejemplo.com', 'Ana', 'Delgado', 'Site Admin', '', 'AM3037'],
      ['rosa.villalobos@ejemplo.com', 'Rosa', 'Villalobos', 'Gerente del socio', 'ORG128', ''],
    ];
    const catalogo: string[][] = [['Tipo', 'Identificador', 'Nombre', 'Pertenece a']];
    for (const o of ds.listOrgs()) {
      catalogo.push([es ? 'Organización' : 'Organization', o.OrgId, o.OrgName, '']);
    }
    for (const s of ds.listSites()) {
      catalogo.push(['Site', s.SiteId, s.SiteName, s.OrgName || s.OrgId]);
    }

    // La primera hoja va SIN título y sin ejemplos: al subirla se lee la
    // fila 1 como cabecera, así que cualquier cosa por encima rompe el
    // archivo — y sería la propia plantilla la que el portal rechaza.
    // Los ejemplos viven en su hoja, que nadie lee al importar.
    downloadSheets(es ? 'plantilla_usuarios' : 'users_template', [
      { name: es ? 'Usuarios' : 'Users', rows: [[...COLUMNAS]] },
      {
        name: es ? 'Ejemplo' : 'Example',
        rows: [[...COLUMNAS], ...ejemplo],
        title: es
          ? 'Esto es solo un ejemplo. Escribe tus filas en la hoja «Usuarios»; los identificadores están en «Catálogo».'
          : 'Example only. Type your rows on the «Users» sheet; identifiers are on «Catálogo».',
      },
      { name: 'Catálogo', rows: catalogo },
    ]);
  }

  async function elegir(f: File | null) {
    setFile(f);
    setFilas(null);
    setPrevio(null);
    setResumen(null);
    if (!f) return;
    try {
      const hoja = await parseSpreadsheet(f);
      const mapa = mapaCabeceras(hoja.headers);
      if (!mapa['Correo'] || !mapa['Rol']) {
        addToast({
          kind: 'err',
          title: es ? 'La plantilla no cuadra' : 'Template mismatch',
          body: es
            ? 'Faltan las columnas «Correo» y «Rol». Descarga la plantilla y rellénala sobre ella.'
            : 'Missing the «Correo» and «Rol» columns. Download the template and fill it in.',
        });
        return;
      }
      const leidas: FilaEnviada[] = [];
      hoja.rows.forEach((r, i) => {
        const email = String(r[mapa['Correo']!] ?? '').trim();
        const rol = String(r[mapa['Rol']!] ?? '').trim();
        const ambito = [
          ...partirIds(mapa['Organización (ATC)'] ? r[mapa['Organización (ATC)']!] : ''),
          ...partirIds(mapa['Sites'] ? r[mapa['Sites']!] : ''),
        ];
        // Una fila del todo vacía no es un error: es el final del archivo.
        if (!email && !rol && ambito.length === 0) return;
        leidas.push({
          // +2: la fila 1 es la cabecera, y las personas cuentan desde 1
          fila: i + 2,
          email,
          first: String(r[mapa['Nombre'] ?? ''] ?? '').trim(),
          last: String(r[mapa['Apellido'] ?? ''] ?? '').trim(),
          rol,
          ambito,
        });
      });
      if (leidas.length === 0) {
        addToast({
          kind: 'warn',
          title: es ? 'El archivo no trae filas' : 'No rows in the file',
          body: es ? 'Solo tiene la cabecera.' : 'It only has the header row.',
        });
        return;
      }
      setFilas(leidas);
    } catch {
      addToast({
        kind: 'err',
        title: es ? 'No se pudo leer el archivo' : 'Could not read the file',
        body: es ? 'Tiene que ser .xlsx, .xls o .csv.' : 'It must be .xlsx, .xls or .csv.',
      });
    }
  }

  async function comprobar() {
    if (!filas) return;
    setBusy(true);
    try {
      const r = await api.post<Previo>('/api/altas/previsualizar', { filas });
      setPrevio(r);
      setResumen(null);
    } catch (err) {
      addToast({
        kind: 'err',
        title: es ? 'No se pudo comprobar' : 'Check failed',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  }

  async function aplicar() {
    if (!filas) return;
    setBusy(true);
    try {
      const r = await api.post<Resumen>('/api/altas/aplicar', {
        filas,
        lote: lote.trim() || undefined,
        restablecer,
        entrega,
      });
      setResumen(r);
      setTimeout(() => panel.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80);
      // Una invitación que no salió deja a esa persona sin forma de entrar,
      // así que no se anuncia como éxito: se dice cuántas y se pide mirar.
      const falladas = r.sinInvitar ?? 0;
      addToast({
        kind: falladas > 0 ? 'warn' : 'ok',
        title: falladas > 0
          ? (es ? 'Cuentas creadas, pero faltan invitaciones' : 'Accounts created, invitations missing')
          : (es ? 'Cuentas dadas de alta' : 'Accounts created'),
        body: es
          ? `${r.creadas} nuevas · ${r.restablecidas} restablecidas · ${r.omitidas} omitidas`
            + (falladas > 0 ? ` · ${falladas} sin invitación` : '')
          : `${r.creadas} new · ${r.restablecidas} reset · ${r.omitidas} skipped`
            + (falladas > 0 ? ` · ${falladas} not invited` : ''),
        ...(falladas > 0
          ? {
              details: es
                ? 'Esas cuentas existen pero no tienen contraseña ni enlace, así que no pueden entrar. '
                  + 'Vuelve a subir la misma plantilla con «Restablecer» marcado para mandarles la invitación otra vez.'
                : 'Those accounts exist but have no password and no link, so they cannot sign in. '
                  + 'Upload the same template again with «Reset» ticked to send their invitation once more.',
            }
          : {}),
        persistente: true,
      });
    } catch (err) {
      addToast({
        kind: 'err',
        title: es ? 'No se dio de alta a nadie' : 'Nothing was created',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  }

  /** Las contraseñas llegaron una vez y no están guardadas en ninguna
   *  parte. Este archivo es la única copia: se entrega y se borra. */
  function descargarResultado() {
    if (!resumen) return;
    const rows: string[][] = [[
      es ? 'Correo' : 'Email', es ? 'Nombre' : 'Name', es ? 'Rol' : 'Role',
      es ? 'Ámbito' : 'Scope', es ? 'Contraseña temporal' : 'Temporary password',
      es ? 'Estado' : 'Status',
    ]];
    for (const f of resumen.filas) {
      rows.push([f.email, f.nombre, f.role, f.ambito, f.password ?? '', f.estado]);
    }
    downloadSheets(`altas_${resumen.lote}`, [{
      name: es ? 'Altas' : 'Accounts',
      rows,
      title: es
        ? 'Lista de credenciales. Entrégala por un canal seguro y bórrala. Cada persona tendrá que cambiar su contraseña al entrar.'
        : 'Credential list. Deliver it securely and delete it. Everyone must change their password on first sign-in.',
    }]);
  }

  /* ---------------- la tabla ----------------

     Comprobar y aplicar enseñan lo mismo: una fila por cada línea del
     archivo, con lo que le pasó. Antes eran dos paneles distintos —una
     lista de errores por un lado y unos contadores por otro— y no había
     forma de mirar una fila concreta y saber qué había sido de ella. */

  const vista = React.useMemo((): Vista | null => {
    if (resumen) {
      const ESTADO = {
        creada: { texto: es ? 'Creada' : 'Created', tono: 'ok' as Tono },
        restablecida: { texto: es ? 'Restablecida' : 'Reset', tono: 'warn' as Tono },
        omitida: { texto: es ? 'Omitida — ya existía' : 'Skipped — already existed', tono: 'neutro' as Tono },
      };
      const falladas = resumen.sinInvitar ?? 0;
      return {
        resumen: [
          { clave: 'creadas', n: resumen.creadas, texto: es ? 'creadas' : 'created', tono: 'ok' },
          { clave: 'restablecidas', n: resumen.restablecidas, texto: es ? 'restablecidas' : 'reset', tono: 'warn' },
          { clave: 'omitidas', n: resumen.omitidas, texto: es ? 'omitidas' : 'skipped', tono: 'neutro' },
          ...(falladas > 0
            ? [{
                clave: 'sin-invitar', n: falladas,
                texto: es ? 'sin invitación' : 'not invited', tono: 'err' as Tono,
              }]
            : []),
        ],
        aviso: falladas > 0
          ? (es
              ? 'Las cuentas marcadas en rojo existen pero no tienen contraseña ni enlace: nadie puede entrar en ellas. Vuelve a subir la misma plantilla con «Restablecer» marcado para mandarles la invitación otra vez.'
              : 'The rows in red exist but have no password and no link: nobody can sign in to them. Upload the same template again with «Reset» ticked to send their invitation once more.')
          : undefined,
        avisoGrave: falladas > 0,
        filas: resumen.filas.map((f, i) => {
          // Por invitación el resultado que importa no es «creada» sino si
          // el correo salió: una cuenta creada cuya invitación falló es una
          // persona que no puede entrar, y decir «creada» lo taparía.
          const invitacionFallida = f.entrega === 'invitacion' && f.invitada === false;
          return {
            clave: `r-${f.email}-${i}`,
            fila: (f.filas ?? []).join(', ') || '—',
            email: f.email,
            nombre: f.nombre,
            rol: ROL_TEXTO(f.role, es),
            ambito: f.ambito,
            estado: invitacionFallida
              ? (es ? 'Creada — NO se pudo invitar' : 'Created — could not invite')
              : ESTADO[f.estado].texto,
            motivo: invitacionFallida
              ? (es
                  ? 'La cuenta existe pero no tiene contraseña ni enlace. No puede entrar.'
                  : 'The account exists but has no password and no link. It cannot sign in.')
              : f.entrega === 'invitacion' && f.estado !== 'omitida'
                ? (es ? 'Invitación enviada por correo.' : 'Invitation sent by email.')
                : undefined,
            tono: invitacionFallida ? ('err' as Tono) : ESTADO[f.estado].tono,
          };
        }),
      };
    }

    if (!previo) return null;

    const deProblemas = previo.problemas.map((p) => ({
      orden: p.fila,
      clave: `p-${p.fila}-${p.email}`,
      fila: String(p.fila),
      email: p.email,
      nombre: '',
      rol: '',
      ambito: '',
      estado: es ? 'No se puede dar de alta' : 'Cannot be created',
      motivo: p.motivo,
      tono: 'err' as Tono,
    }));

    /** Qué tiene hoy la cuenta, y qué se le quita.
     *
     *  Sin esto la tabla solo enseñaba lo propuesto, y como el ámbito se
     *  reemplaza entero, un recorte —de tres organizaciones a una— no se
     *  veía en ninguna parte hasta después de hacerlo. */
    const queTieneHoy = (c: CandidatoVista): string | undefined => {
      if (!c.existe || !c.actual) return undefined;
      const rolHoy = ROL_TEXTO(c.actual.role, es);
      const hoy = c.actual.ambito.length ? c.actual.ambito.join(' ') : (es ? 'sin ámbito' : 'no scope');
      const cabecera = es
        ? `Hoy: ${rolHoy}, ${hoy}.`
        : `Today: ${rolHoy}, ${hoy}.`;
      if (!restablecer) return `${cabecera} ${es ? 'No se tocará.' : 'Will not be touched.'}`;
      const perdidos = c.actual.ambito.filter((a) => !c.ambito.includes(a));
      const cambiaRol = c.actual.role !== c.role;
      const avisos = [
        perdidos.length
          ? (es ? `Perderá ${perdidos.join(' ')}.` : `Will lose ${perdidos.join(' ')}.`)
          : '',
        cambiaRol
          ? (es ? `Pasará a ${ROL_TEXTO(c.role, es)}.` : `Will become ${ROL_TEXTO(c.role, es)}.`)
          : '',
      ].filter(Boolean);
      return [cabecera, ...avisos].join(' ');
    };

    const deCandidatos = previo.candidatos.map((c) => ({
      orden: c.filas[0] ?? 0,
      clave: `c-${c.email}`,
      fila: c.filas.join(', ') || '—',
      email: c.email,
      nombre: c.nombre,
      rol: ROL_TEXTO(c.role, es),
      ambito: c.ambito.join(' '),
      estado: !c.existe
        ? (es ? 'Se creará' : 'Will be created')
        : restablecer
          ? (es ? 'Ya existe — se restablecerá' : 'Exists — will be reset')
          : (es ? 'Ya existe — se omitirá' : 'Exists — will be skipped'),
      motivo: queTieneHoy(c),
      tono: (!c.existe ? 'ok' : restablecer ? 'warn' : 'neutro') as Tono,
    }));

    const hayProblemas = previo.problemas.length > 0;
    return {
      resumen: [
        { clave: 'nuevas', n: previo.nuevas, texto: es ? 'se crearán' : 'will be created', tono: 'ok' },
        { clave: 'existen', n: previo.existentes, texto: es ? 'ya existen' : 'already exist', tono: 'warn' },
        { clave: 'malas', n: previo.problemas.length, texto: es ? 'con problemas' : 'with problems', tono: 'err' },
      ],
      aviso: hayProblemas
        ? (es
            ? 'Corrige estas filas en la plantilla y vuelve a subirla. Mientras haya una sola mal, no se da de alta a nadie.'
            : 'Fix these rows in the template and upload it again. While one is wrong, nobody is created.')
        : previo.existentes > 0 && !restablecer
          ? (es
              ? 'Las cuentas que ya existen se omiten. Si quieres cambiarles la contraseña y el ámbito, marca «Restablecer» y vuelve a comprobar.'
              : 'Existing accounts are skipped. Tick «Reset» and check again to change their password and scope.')
          : undefined,
      avisoGrave: hayProblemas,
      filas: [...deProblemas, ...deCandidatos].sort((a, b) => a.orden - b.orden),
    };
  }, [previo, resumen, restablecer, es]);

  const puedeComprobar = Boolean(filas?.length) && !busy;
  // Si todas las cuentas ya existen y no se pidió restablecer, pulsar
  // «Dar de alta» no haría nada: dejarlo encendido solo sirve para
  // devolver un «0 creadas» que parece un fallo.
  const puedeAplicar =
    Boolean(previo) && !busy
    && previo!.problemas.length === 0
    && previo!.candidatos.length > 0
    && (previo!.nuevas > 0 || restablecer);

  return (
    <Section
      title={es ? 'Alta de cuentas en lote' : 'Bulk account creation'}
      badge={filas ? `${filas.length}` : undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', maxWidth: '68ch' }}>
          {es
            ? 'Descarga la plantilla, rellénala y súbela. Se crean las cuentas, se les asigna su organización y sus sites, y se genera una contraseña temporal para cada una. Nadie se queda con esa contraseña: el portal obliga a cambiarla en el primer acceso.'
            : 'Download the template, fill it in and upload it. Accounts are created with their organization and sites, and a temporary password is generated for each. Nobody keeps it: the portal forces a change on first sign-in.'}
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn" onClick={descargarPlantilla}>
            {sized(ic.download, 14)} {es ? 'Descargar plantilla' : 'Download template'}
          </button>
          <label className="btn" style={{ cursor: 'pointer' }}>
            {sized(ic.upload, 14)} {file ? file.name : (es ? 'Elegir archivo' : 'Choose file')}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={(e) => void elegir(e.target.files?.[0] ?? null)}
            />
          </label>
          {filas ? (
            <span className="chip">{filas.length} {es ? 'filas leídas' : 'rows read'}</span>
          ) : null}
        </div>

        {filas ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              style={{ maxWidth: 220 }}
              placeholder={es ? 'Nombre del lote (opcional)' : 'Batch name (optional)'}
              value={lote}
              onChange={(e) => setLote(e.target.value)}
            />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={restablecer}
                onChange={(e) => setRestablecer(e.target.checked)}
              />
              {es
                ? 'Restablecer las cuentas que ya existan'
                : 'Reset accounts that already exist'}
            </label>
            <button className="btn" disabled={!puedeComprobar} onClick={() => void comprobar()}>
              {es ? 'Comprobar' : 'Check'}
            </button>
            <button className="btn primary" disabled={!puedeAplicar} onClick={() => void aplicar()}>
              {es ? 'Dar de alta' : 'Create accounts'}
            </button>
          </div>
        ) : null}

        {/* Cómo se entrega la cuenta. Solo aparece cuando el portal puede
            mandar correo: sin él no hay elección que ofrecer, y un
            desplegable con una sola opción viable confunde. */}
        {filas && previo?.puedeInvitar ? (
          <div
            style={{
              display: 'flex', flexDirection: 'column', gap: 7,
              padding: '11px 13px', borderRadius: 9,
              border: '1px solid var(--line)', background: 'var(--surface-2)',
            }}
          >
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              {es ? 'Cómo entra cada persona' : 'How each person signs in'}
            </span>
            <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, fontSize: 13, lineHeight: 1.5 }}>
              <input
                type="radio"
                name="entrega"
                checked={entrega === 'invitacion'}
                onChange={() => setEntrega('invitacion')}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{es ? 'Invitación por correo' : 'Invitation by email'}</strong>
                <span style={{ color: 'var(--text-2)' }}>
                  {es
                    ? ' — cada persona recibe un enlace y elige su propia contraseña. Nadie de ETC llega a conocerla.'
                    : ' — each person receives a link and chooses their own password. Nobody at ETC ever knows it.'}
                </span>
              </span>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, fontSize: 13, lineHeight: 1.5 }}>
              <input
                type="radio"
                name="entrega"
                checked={entrega === 'temporal'}
                onChange={() => setEntrega('temporal')}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{es ? 'Contraseñas temporales' : 'Temporary passwords'}</strong>
                <span style={{ color: 'var(--text-2)' }}>
                  {es
                    ? ' — el portal las genera y tú las entregas por tu canal. Útil si alguien no tiene correo fiable.'
                    : ' — the portal generates them and you deliver them yourself. Useful if someone has no reliable email.'}
                </span>
              </span>
            </label>
          </div>
        ) : null}

        {vista ? (
          <div ref={panel} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
              {vista.resumen.map((r) => (
                <span
                  key={r.clave}
                  className="chip"
                  style={{
                    color: r.n ? COLOR[r.tono] : 'var(--text-3)',
                    borderColor: r.n && r.tono !== 'neutro' ? COLOR[r.tono] : undefined,
                    fontWeight: r.n ? 600 : 400,
                  }}
                >
                  {r.n} {r.texto}
                </span>
              ))}
              {resumen ? (
                <span className="chip mono" style={{ color: 'var(--text-3)' }}>
                  {es ? 'lote' : 'batch'} {resumen.lote}
                </span>
              ) : null}
            </div>

            {vista.aviso ? (
              <p
                style={{
                  margin: 0, fontSize: 12.5, lineHeight: 1.5,
                  color: vista.avisoGrave ? 'var(--err)' : 'var(--text-2)',
                  fontWeight: vista.avisoGrave ? 600 : 400,
                }}
              >
                {vista.aviso}
              </p>
            ) : null}

            <div style={{ overflowX: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-2)' }}>
                    {[
                      es ? 'Fila' : 'Row',
                      es ? 'Correo' : 'Email',
                      es ? 'Nombre' : 'Name',
                      es ? 'Rol' : 'Role',
                      es ? 'Ámbito' : 'Scope',
                      es ? 'Resultado' : 'Outcome',
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left', padding: '8px 12px', fontWeight: 600,
                          fontSize: 11, letterSpacing: '.04em', textTransform: 'uppercase',
                          color: 'var(--text-3)', whiteSpace: 'nowrap',
                          borderBottom: '1px solid var(--line)',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vista.filas.map((f) => (
                    <tr key={f.clave} style={{ borderTop: '1px solid var(--line)' }}>
                      <td className="mono" style={{ padding: '8px 12px', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                        {f.fila}
                      </td>
                      <td className="mono" style={{ padding: '8px 12px' }}>{f.email || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{f.nombre || '—'}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{f.rol || '—'}</td>
                      <td className="mono" style={{ padding: '8px 12px' }}>{f.ambito || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ color: COLOR[f.tono], fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {f.estado}
                        </span>
                        {f.motivo ? (
                          <div style={{ color: 'var(--text-2)', fontWeight: 400, marginTop: 3, maxWidth: '46ch' }}>
                            {f.motivo}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Solo cuando de verdad hay contraseñas que entregar. Por
                invitación no se genera ninguna, y ofrecer «descargar
                contraseñas» para bajar una columna vacía haría dudar de si
                el alta funcionó. */}
            {resumen?.filas.some((f) => f.password) ? (
              <div
                className="card"
                style={{ padding: '11px 13px', borderLeft: '3px solid var(--ok)' }}
              >
                <p style={{ margin: '0 0 9px', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  {es
                    ? 'Las contraseñas se generaron ahora y no se guardan en ningún sitio. Descarga el archivo antes de salir de esta pantalla: es la única copia. Si lo pierdes, vuelve a subir la plantilla con «Restablecer» marcado.'
                    : 'The passwords were just generated and are stored nowhere. Download the file before leaving this screen: it is the only copy.'}
                </p>
                <button className="btn primary" onClick={descargarResultado}>
                  {sized(ic.download, 14)} {es ? 'Descargar contraseñas' : 'Download passwords'}
                </button>
              </div>
            ) : resumen && resumen.creadas + resumen.restablecidas > 0 ? (
              <div
                className="card"
                style={{ padding: '11px 13px', borderLeft: '3px solid var(--ok)' }}
              >
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  {es
                    ? 'No hay contraseñas que entregar: cada persona recibió un enlace por correo y elige la suya. El enlace caduca solo, y si alguien no lo recibe puede pedir otro desde «¿Olvidaste tu contraseña?».'
                    : 'There are no passwords to hand out: each person received a link by email and chooses their own. The link expires on its own, and anyone who does not receive it can request another from «Forgot your password?».'}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Section>
  );
}
